import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toast } from '@livable/saas-react-components';
import { useEffect, useRef, useState } from 'react';
import { useStore } from 'src/stores';
import { APPFOLIO_PUSH_ATTEMPT_STATUS, APPFOLIO_PUSH_REFUSAL_CODES, APPFOLIO_PUSH_STATUS, getLiveAttempt, isSameBillId, PUSHABLE_STATUSES } from './appfolioPushConstants';
import { isInvoiceNumberFixed, resolveApPushInvoiceNumber } from './apPushInvoiceNumber';

/** Extraction status the document has to reach before the service accepts a push. */
const VALIDATED_BILL_STATUS = 'Validated';

/**
 * The trigger is async: it queues the payable and answers immediately. The contract asks the UI not to
 * poll, so a queued push is followed by exactly one delayed status read.
 */
const STATUS_REFETCH_DELAY_MS = 5000;

/**
 * Drives the "Push AP to AppFolio" control for one uploaded document: reads its push status on open,
 * triggers the push, and keeps whatever the service answered for the form to render.
 */
export const useAppfolioPush = _ref => {
  let billId = _ref.billId,
    billStatus = _ref.billStatus,
    extraction = _ref.extraction,
    isEnabled = _ref.isEnabled,
    typedInvoiceNumber = _ref.typedInvoiceNumber;
  const store = useStore();
  const refetchTimeoutRef = useRef(null);

  /**
   * A payable is identified by its invoice number, and the service refuses a push without one. Deciding
   * it here rather than at the button keeps the control, the reason it is inert, and what the trigger
   * sends as one answer — three places that must not be able to disagree.
   */
  const invoiceNumber = resolveApPushInvoiceNumber({
    extraction,
    typedInvoiceNumber
  });
  const _useState = useState({
      /** `statuses[]` entry for this document; null while it has not been read. */
      pushStatus: null,
      /** True once a status read came back, so an absent document can be told from an unread one. */
      hasReadStatus: false,
      /** Blockers of the live attempt — always rendered with their `detail`, which names the way out. */
      blockers: [],
      /** `error.errorCode` of the last refused trigger (422). */
      refusalCode: null
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const clearScheduledRefetch = () => {
    if (!refetchTimeoutRef.current) return;
    clearTimeout(refetchTimeoutRef.current);
    refetchTimeoutRef.current = null;
  };
  const readStatus = async () => {
    var _find, _resp$response$status, _resp$response;
    if (!billId) return;
    const resp = await store.billAutomationBillsApi.getAppfolioPushStatusAsync.run({
      billIds: [billId]
    });
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return;

    // Documents the company does not own are omitted entirely, which is not the same as "not pushed".
    const pushStatus = (_find = ((_resp$response$status = (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.statuses) !== null && _resp$response$status !== void 0 ? _resp$response$status : []).find(item => isSameBillId(item.billId, billId))) !== null && _find !== void 0 ? _find : null;
    setState(prev => {
      var _getLiveAttempt$block, _getLiveAttempt;
      return _objectSpread(_objectSpread({}, prev), {}, {
        pushStatus,
        hasReadStatus: true,
        blockers: (_getLiveAttempt$block = (_getLiveAttempt = getLiveAttempt(pushStatus)) === null || _getLiveAttempt === void 0 ? void 0 : _getLiveAttempt.blockers) !== null && _getLiveAttempt$block !== void 0 ? _getLiveAttempt$block : []
      });
    });
  };
  useEffect(() => {
    if (!isEnabled || !billId) return undefined;
    readStatus();
    return clearScheduledRefetch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, billId]);

  /** `overrideTotalAmount` is only present when a person edited the Bill total field by hand. */
  const handlePush = async overrideTotalAmount => {
    var _resp$response$attemp, _resp$response2;
    if (!billId) return;
    clearScheduledRefetch();
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      blockers: [],
      refusalCode: null
    }));
    const resp = await store.billAutomationBillsApi.pushBillToAppfolioAsync.run({
      billId,
      overrideTotalAmount,
      // Sent only when it is this side's contribution: a confidently extracted document identifies
      // itself, and the service would ignore a number restated over it.
      invoiceNumber: invoiceNumber.invoiceNumber
    });
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) {
      var _store$billAutomation, _store$billAutomation2;
      // The async model has already raised the server's own message; the code drives what the form shows.
      const refusalCode = (_store$billAutomation = (_store$billAutomation2 = store.billAutomationBillsApi.pushBillToAppfolioAsync.error) === null || _store$billAutomation2 === void 0 ? void 0 : _store$billAutomation2.errorCode) !== null && _store$billAutomation !== void 0 ? _store$billAutomation : null;
      setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        refusalCode
      }));

      // The refusal carries no date, but the status read does — and it also settles the button state.
      if (refusalCode === APPFOLIO_PUSH_REFUSAL_CODES.billAlreadyPushed) readStatus();
      return;
    }

    // A trigger either records exactly one attempt or refuses with a 422.
    const attempt = (_resp$response$attemp = (_resp$response2 = resp.response) === null || _resp$response2 === void 0 || (_resp$response2 = _resp$response2.attempts) === null || _resp$response2 === void 0 ? void 0 : _resp$response2[0]) !== null && _resp$response$attemp !== void 0 ? _resp$response$attemp : null;
    if ((attempt === null || attempt === void 0 ? void 0 : attempt.status) === APPFOLIO_PUSH_ATTEMPT_STATUS.blocked) {
      setState(prev => {
        var _attempt$blockers, _prev$pushStatus;
        return _objectSpread(_objectSpread({}, prev), {}, {
          blockers: (_attempt$blockers = attempt.blockers) !== null && _attempt$blockers !== void 0 ? _attempt$blockers : [],
          pushStatus: _objectSpread(_objectSpread({}, (_prev$pushStatus = prev.pushStatus) !== null && _prev$pushStatus !== void 0 ? _prev$pushStatus : {}), {}, {
            billId,
            status: APPFOLIO_PUSH_STATUS.failed,
            attempts: [attempt]
          }),
          hasReadStatus: true
        });
      });
      return;
    }
    setState(prev => {
      var _prev$pushStatus2;
      return _objectSpread(_objectSpread({}, prev), {}, {
        pushStatus: _objectSpread(_objectSpread({}, (_prev$pushStatus2 = prev.pushStatus) !== null && _prev$pushStatus2 !== void 0 ? _prev$pushStatus2 : {}), {}, {
          billId,
          status: APPFOLIO_PUSH_STATUS.pending,
          attempts: [attempt]
        }),
        hasReadStatus: true
      });
    });
    toast('success', {
      body: ['Push requested — the status will update here shortly']
    });
    refetchTimeoutRef.current = setTimeout(readStatus, STATUS_REFETCH_DELAY_MS);
  };
  const pushStatus = state.pushStatus,
    hasReadStatus = state.hasReadStatus;
  const isPushing = store.billAutomationBillsApi.pushBillToAppfolioAsync.inProgress;
  /** A document the read did not cover is foreign or unknown, so it is never pushable from here. */
  const isUnknownDocument = hasReadStatus && !pushStatus;

  /** `feature_disabled` means the environment does not offer the push at all: drop the control. */
  const isVisible = isEnabled && state.refusalCode !== APPFOLIO_PUSH_REFUSAL_CODES.featureDisabled;
  const canPush = isVisible && !!billId && billStatus === VALIDATED_BILL_STATUS && !isPushing && !isUnknownDocument &&
  // A document pushed from elsewhere is settled by its duplicate link, whatever its own status says.
  !(pushStatus !== null && pushStatus !== void 0 && pushStatus.duplicateOfBillId) && invoiceNumber.canPush && (!pushStatus || PUSHABLE_STATUSES.includes(pushStatus.status));

  /**
   * Why the control is inert, for the states that have no message of their own. `succeeded`, a duplicate
   * link and `needsReconciliation` already say it themselves, so they stay silent here.
   */
  const getDisabledReason = () => {
    if (!isVisible || canPush || !billId) return null;
    if (billStatus !== VALIDATED_BILL_STATUS) {
      return 'The bill data has to be validated before it can be pushed to AppFolio.';
    }
    if (isUnknownDocument) return 'The AppFolio push status is not available for this bill.';
    if ((pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.status) === APPFOLIO_PUSH_STATUS.pending) {
      return 'This bill is on its way to AppFolio. The status will update here when it lands.';
    }
    return invoiceNumber.reason;
  };
  return {
    blockers: state.blockers,
    canPush,
    /**
     * Whether the extraction's own invoice number stands, leaving the field nothing to accept.
     *
     * Gated on the control being offered at all: with no push there is nothing that would ignore an
     * edit, so locking the field would take something away for no reason a person could act on.
     */
    isInvoiceNumberFixed: isVisible && isInvoiceNumberFixed(extraction),
    disabledReason: getDisabledReason(),
    handlePush,
    isPushing,
    isVisible,
    liveAttempt: getLiveAttempt(pushStatus),
    pushStatus,
    refusalCode: state.refusalCode
  };
};